'use client'
import React, { useEffect } from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import { useModalController } from './useModalController';
import { RiCloseLine } from '@remixicon/react';
import { Button } from '@/components/atoms';
import { createPortal } from 'react-dom';


export interface IModalProps {
    title: string;
    controller: ReturnType<typeof useModalController>;
    children: React.ReactNode;
    overlayClassName?: string;
    contentClassName?: string;
    bodyClassName?: string;
    bottomClassName?: string;

    actionButtons?: React.ReactNode;
    overlayBlurEffect?: boolean;
    closeOnOverlayClick?: boolean;
    closeOnEsc?: boolean;
    hideActionButtons?: boolean;
    hideHeader?: boolean;
    headerClassName?: string;
    titleClassName?: string;
}
const Modal = ({
    title, controller, children,
    overlayClassName, contentClassName, bodyClassName,
    bottomClassName, actionButtons, closeOnOverlayClick, closeOnEsc,
    overlayBlurEffect = true,
    hideActionButtons = false,
    hideHeader = false,
    headerClassName,
    titleClassName

}: IModalProps) => {

    const handleOverlayClick = (event: React.MouseEvent) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
            controller.close();
        }
    };

    const escHandler = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            controller.close();
        }
    }
    useEffect(() => {
        if (closeOnEsc && controller.isOpen) {
            document.addEventListener('keydown', escHandler);
        }
        if (closeOnEsc && !controller.isOpen) {
            document.removeEventListener('keydown', escHandler);
        }


        return () => {
            document.removeEventListener('keydown', escHandler);
        }
    }, [closeOnEsc, controller])

    if (!controller.isOpen) return null;


    return createPortal(
        <div onClick={handleOverlayClick} onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className={twMerge(classNames(
            "fixed", "inset-0", "flex", "items-center", "justify-center",
            "z-999",
            {
                "backdrop-blur-sm bg-[rgba(0,0,0,0.15)]": overlayBlurEffect,
                "bg-[rgba(0,0,0,0.3)]": !overlayBlurEffect
            }), overlayClassName)}>
            <div className={twMerge(classNames("bg-white overflow-hidden rounded-lg drop-shadow-2xl shadow-xl max-w-md w-full mx-4 relative"), bodyClassName)}>

                <div className="max-w-full">
                    {
                        !hideHeader && (
                            <div className={classNames(
                                "flex", "items-center", "justify-between",
                                "px-5 py-4", "border-b", "border-gray-200",

                                headerClassName
                            )}>
                                <h2 className={twMerge(classNames("text-lg font-medium text-dashboard-title"), titleClassName)} >{title}</h2>
                                <button className="cursor-pointer" onClick={controller.close}>
                                    <RiCloseLine className="w-5 h-5" />
                                </button>
                            </div>
                        )
                    }



                    <main className={twMerge(classNames("p-5"), contentClassName)}>
                        {children}
                    </main>


                    {
                        !hideActionButtons && (
                            <div className={twMerge(classNames("flex space-x-2 px-5 py-4 justify-end bg-slate-100 border-t border-slate-200"), bottomClassName)}>
                                {
                                    actionButtons ? actionButtons : (
                                        <>
                                            <Button variant='secondary' onClick={controller.close}> Cancel </Button>
                                        </>
                                    )
                                }
                            </div>
                        )
                    }
                </div>
            </div>
        </div>,
        document.body
    )
};




export { Modal }